import type { SuperadminCompanyPermission, UserPermission } from 'api/models/mainservice';
import type { StorePermission } from 'api/models/unified';
import { useGetCompanyUserPermissionsQuery } from 'pages/admin/companies/queries';
import { useGetStoresV2Query } from 'pages/material/queries';
import { useMemo } from 'react';

import useAuth from './useAuth';

export const usePermissions = () => {
  const { userPermissions } = useAuth();

  const can = useMemo(
    () => (requestedPermissions: UserPermission[]) => {
      return requestedPermissions.some((requested) => userPermissions?.includes(requested));
    },
    [userPermissions]
  );

  return { can };
};

export const useSuperadminCompanyPermissions = (companyId: string) => {
  const { userPermissions, user } = useAuth();
  const userCompanyPermissionsQuery = useGetCompanyUserPermissionsQuery(companyId);

  const superadminPermissions = userPermissions;

  const can = useMemo(
    () => (requestedPermissions: (UserPermission | SuperadminCompanyPermission)[]) => {
      const userCompanyPermissions =
        userCompanyPermissionsQuery.data?.userSuperadminPermissions.find(
          (x) => x.userId === user?.id
        )?.permissions || [];

      const mergedPermissions = [...superadminPermissions, ...userCompanyPermissions];

      return requestedPermissions.some((requested) => mergedPermissions?.includes(requested));
    },
    [superadminPermissions, userCompanyPermissionsQuery.data, user?.id]
  );

  return { can };
};

export const useStorePermissions = (storeId?: string) => {
  const { user, userPermissions } = useAuth();

  const stores = useGetStoresV2Query();

  const can = useMemo(
    () => (requestedPermissions: (UserPermission | StorePermission)[]) => {
      const storeUserPermission = stores.data
        ?.find((s) => s.id === storeId)
        ?.availableFor.find((available) => available.userId === user?.id)?.permissionLevel;

      const mergedPermissions = storeUserPermission
        ? [...userPermissions, storeUserPermission]
        : userPermissions;

      return requestedPermissions.some((requested) => mergedPermissions?.includes(requested));
    },
    [storeId, stores.data, user, userPermissions]
  );

  return { can };
};
